import { isEmpty, isObject } from 'lodash';

import { LeadSubmissionBuilder, LEAD_TYPES } from '@sm360/widget-commons-leads';
import { TradeInsService } from '@sm360/widget-commons-vehicle';
import { UrlService } from '@sm360/widget-commons-core-services';

/**
 * Service that handles the logic with the creation and update of the Trade In depending of the situation
 * (User logged or not, lead created or not, etc.)
 */
class TradeInService {
    constructor() {
        /**
         * @type {Object} The ShowroomAPI TradeIn object
         */
        this.tradeIn = null;
    }

    /**
     * Fetch on the ShowroomAPI the tradeIn for the logger account and set it as a class variable
     * @return {Promise} A Promise the gives the TradeIn
     */
    fetchTradeIn(config) {
        return TradeInsService.loadTradeInVehicle(config).then((tradeIn) => {
            delete tradeIn.dealerIds;
            this.tradeIn = tradeIn;
            return Promise.resolve(tradeIn);
        });
    }

    /**
     * Saves the trade in in the logged account and create/update the lead with the trade in information
     * The logic is copied from saveTradeIn function below with some modification as per need
     * TODO: Depreciate the saveTradeIn if agreed to use only new tradeIn widget for all clients
     * @param {TradeInModelNew} tradeIn
     * @param {QuoteModel} quote
     * @param {boolean} detectLeadTypeNew try to detect if the current URL is related to a new vehicle
     *                                    if so, we use the "Trade-in Appraisal (New)" lead type
     * @returns {Promise} the returns the created/updated `TradeInModelNew`
     */
    saveTradeInNew(tradeIn, quote, detectLeadTypeNew = false, config, asUniqueLead) {
        // we recieve tradeIN model, so let's build good tradeIn for microservice
        // save tradein directly in tradein mircoservice if there is tradein id
        if (tradeIn.id) {
            const tradeInToSave = {
                id: tradeIn.id,
            };

            if (tradeIn.leadId) {
                tradeInToSave.leadId = tradeIn.leadId;
            }

            if (!isEmpty(tradeIn.tradeInVehicle)) {
                const {
                    id: vehicleId,
                    make,
                    model,
                    year,
                    trim,
                    vin,
                    odometer: kilometers,
                    style: bodyStyle,
                    exteriorColor,
                    interiorColor,
                    details,
                } = tradeIn.tradeInVehicle;

                tradeInToSave.vehicleId = vehicleId;
                tradeInToSave.make = make;
                tradeInToSave.model = model;
                tradeInToSave.trim = trim;
                tradeInToSave.year = year;
                tradeInToSave.vin = vin;
                tradeInToSave.kilometers = kilometers;
                tradeInToSave.bodyStyle = bodyStyle;
                tradeInToSave.exteriorColor = exteriorColor;
                tradeInToSave.interiorColor = interiorColor;
                tradeInToSave.details = details;
            }

            if (!isEmpty(tradeIn.estimation)) {
                tradeInToSave.estimation = tradeIn.estimation;
            }

            if (!isEmpty(tradeIn.evaluationQuestion)) {
                tradeInToSave.conditionForm = tradeIn.evaluationQuestion;
            }

            if (!isEmpty(tradeIn.appointmentDate)) {
                tradeInToSave.appointmentDate = tradeIn.appointmentDate;
            }

            return TradeInsService.updateTradeIn(tradeInToSave, config).then((updatedTradeIn) => {
                return updatedTradeIn;
            });
        }

        return this.leadSaveTradeInNew(tradeIn, quote, detectLeadTypeNew, config, asUniqueLead).then((response) => {
            // TO DO: tradeInId doesn't even exist in response
            // const { tradeInId } = response;
            // return tradeIn.set('id', tradeInId);
            return response;
        });
    }

    /**
     * Saves the trade in in the logged account and create/update the lead with the trade in information
     * @param {TradeInModel} tradeIn
     * @returns {Promise} the returns the created/updated `TradeInModel`
     */
    saveTradeIn(tradeIn, quote, asUniqueLead, config) {
        // we recieve tradeIN model, so let's build good tradeIn for microservice
        // save tradein directly in tradein mircoservice if there is tradein id
        if (tradeIn.id) {
            const tradeInToSave = {
                id: tradeIn.id,
            };

            if (tradeIn.leadId) {
                tradeInToSave.leadId = tradeIn.leadId;
            }

            if (!isEmpty(tradeIn.tradeInVehicle)) {
                const {
                    id: vehicleId,
                    make,
                    model,
                    year,
                    trim,
                    vin,
                    odometer: kilometers,
                    style: bodyStyle,
                    exteriorColor,
                } = tradeIn.tradeInVehicle;

                tradeInToSave.vehicleId = vehicleId;
                tradeInToSave.make = make;
                tradeInToSave.model = model;
                tradeInToSave.trim = trim;
                tradeInToSave.year = year;
                tradeInToSave.vin = vin;
                tradeInToSave.kilometers = kilometers;
                tradeInToSave.bodyStyle = bodyStyle;
                tradeInToSave.exteriorColor = exteriorColor;
            }

            if (!isEmpty(tradeIn.estimation)) {
                tradeInToSave.estimation = tradeIn.estimation;
            }

            if (!isEmpty(tradeIn.evaluationQuestion)) {
                tradeInToSave.conditionForm = tradeIn.evaluationQuestion;
            }
            return TradeInsService.updateTradeIn(tradeInToSave, config).then((updatedTradeIn) => {
                return updatedTradeIn;
            });
        }

        return this.leadSaveTradeIn(tradeIn, quote, asUniqueLead, config).then((response) => {
            const { tradeInId } = response;
            return tradeIn.set('id', tradeInId);
        });
    }

    /**
     * If set, returns the TradeIn otherwise fetch it first
     * @return {Promise} A Promise the gives the TradeIn
     */
    getTradeIn(config) {
        if (this.tradeIn) {
            return Promise.resolve(this.tradeIn);
        }
        return this.fetchTradeIn(config);
    }

    /**
     * leadSaveTradeInNew will only use for new trade-in
     * @param {TradeInModelNew} tradeIn
     * @param {QuoteModel} quote
     * @param {boolean} detectLeadTypeNew try to detect if the current URL is related to a new vehicle
     *                                    if so, we use the "Trade-in Appraisal (New)" lead type
     */
    leadSaveTradeInNew(tradeIn, quote, detectLeadTypeNew = false, config, asUniqueLead) {
        const isVehicleNew =
            (quote && quote.vehicle && quote.vehicle.getIsVehicleNew && quote.vehicle.getIsVehicleNew()) ||
            (detectLeadTypeNew && UrlService.isPageRelatedToNewVehicle());

        const { firstname, lastname, email, optin, mobilePhone } = tradeIn.profile;

        const {
            make,
            model,
            trim,
            year,
            style,
            odometer,
            exteriorColor,
            interiorColor,
            details,
            vin,
        } = tradeIn.tradeInVehicle;
        const leadSubmissionBuilder = new LeadSubmissionBuilder(config)
            .addContactInformation(firstname, lastname, undefined, email, undefined, mobilePhone)
            .addTradeInVehicle({
                year: parseInt(year, 10),
                make,
                model,
                trim,
                bodyStyle: style,
                exteriorColor: exteriorColor?.description,
                interiorColor: interiorColor?.description,
                kilometers: parseInt(odometer, 10),
                details,
                vin,
            });
        if (asUniqueLead) {
            leadSubmissionBuilder.addLeadType(isVehicleNew ? LEAD_TYPES.TRADE_IN_NEW : LEAD_TYPES.TRADE_IN_USED);
        } else {
            leadSubmissionBuilder.addLeadType(LEAD_TYPES.ACCOUNT_CREATION);
        }

        if (!isEmpty(quote)) {
            leadSubmissionBuilder.addQuote(quote);
        }

        if (isObject(tradeIn?.evaluationQuestion)) {
            leadSubmissionBuilder.addTradeInEvaluation(tradeIn.evaluationQuestion);
        }

        // amontWasUpdated = false -> need only for V2 sites when account is authenticated
        // amountwasUpdated = true for all cases
        if (!isEmpty(tradeIn.estimation)) {
            const { amount, high, low, amountWasUpdated = !asUniqueLead } = tradeIn.estimation;
            leadSubmissionBuilder.addTradeInEstimation(
                parseInt(amount, 10),
                parseInt(low || amount, 10),
                parseInt(high || amount, 10),
                amountWasUpdated
            );
        }
        // Add extra info in basic info textbox in lead
        if (!isEmpty(tradeIn.info)) {
            leadSubmissionBuilder.addBasicInfo(tradeIn.info);
        }
        // TODO: Add vehicle Condition question answers part here
        // if (!isEmpty(tradeIn.evaluationQuestion)) {
        //     leadSubmissionBuilder.addTradeInEvaluation(tradeIn.conditions);
        // }

        if (quote) {
            leadSubmissionBuilder.addVehicle(quote);
        } else {
            // Delete empty vehicle object to avoid adding empty vehicle on lead update for tradeIn
            leadSubmissionBuilder.deleteVehicle();
        }

        if (optin) {
            leadSubmissionBuilder.addOptInEmail(optin);
        }

        return leadSubmissionBuilder.submit(asUniqueLead);
    }

    leadSaveTradeIn(tradeIn, quote, asUniqueLead, config) {
        const isVehicleNew = quote && quote.vehicle && quote.vehicle.getIsVehicleNew();

        const { firstname, lastname, email, homePhone, phoneNumber } = tradeIn.profile;
        const { make, model, trim, style, year, odometer, exteriorColor, vin } = tradeIn.tradeInVehicle;

        const leadSubmissionBuilder = new LeadSubmissionBuilder(config)
            .addLeadType(isVehicleNew ? LEAD_TYPES.TRADE_IN_NEW : LEAD_TYPES.TRADE_IN_USED)
            // Added lead type to account creation to update account creation lead
            // .addLeadType(LEAD_TYPES.ACCOUNT_CREATION)
            .addContactInformation(firstname, lastname, phoneNumber || homePhone, email)
            .addTradeInVehicle({
                year: parseInt(year, 10),
                make,
                model,
                trim,
                bodyStyle: style,
                exteriorColor,
                kilometers: parseInt(odometer, 10),
                vin,
            });

        if (asUniqueLead) {
            leadSubmissionBuilder.addLeadType(isVehicleNew ? LEAD_TYPES.TRADE_IN_NEW : LEAD_TYPES.TRADE_IN_USED);
        } else {
            leadSubmissionBuilder.addLeadType(LEAD_TYPES.ACCOUNT_CREATION);
        }

        if (!isEmpty(tradeIn.estimation)) {
            const { amount, amountWasUpdated = !asUniqueLead, high, low } = tradeIn.estimation;
            leadSubmissionBuilder.addTradeInEstimation(
                parseInt(amount, 10),
                parseInt(low, 10),
                parseInt(high, 10),
                amountWasUpdated
            );
        }

        if (!isEmpty(tradeIn.evaluationQuestion)) {
            leadSubmissionBuilder.addTradeInEvaluation(tradeIn.evaluationQuestion);
        }

        if (quote) {
            leadSubmissionBuilder.addVehicle(quote);
        } else {
            // Delete empty vehicle object to avoid adding empty vehicle on lead update for tradeIn
            leadSubmissionBuilder.deleteVehicle();
        }

        return leadSubmissionBuilder.submit(asUniqueLead);
    }
}

export default new TradeInService();
